/*
  CONFIGURATOR CAPTIONS.

  Writes a permanent "name + price" caption under every option swatch in the
  MageWorx Advanced Product Options app, so a shopper can read what they are
  choosing without hovering and without committing to a selection.

  Client, 2026-08-20: "The labels and enlarged previews only show on
  hover/selection, so on mobile customers can't see what they're picking unless
  they commit. Let's either show the label as a static caption under each swatch,
  or let tapping open a preview without auto-selecting it. Also the grills need to
  have a way to show more info ... I think we show them as rectangular photos with
  name + price underneath instead of cropped circles."

  WHERE THE DATA COMES FROM. The rendered DOM does not contain the name or the
  price - a swatch is a hidden radio plus an <img>, and the only human-readable
  string is `img[alt]`. The app does however expose a JS API, confirmed on the
  live product page:

    var inst = window.mwApo.getInstance();
    inst.getOptions()          -> [{ id, title, type, required, hidden, valueIds }]
    inst.getValues(optionId)   -> [{ id, optionId, title, price, selected,
                                     available, hiddenByRule }]
    inst.on('change', fn)      -> fires on every selection
    inst.getSelections()       -> ["v_21120366", ...]

  `title` is preferred over `img[alt]` because it is the option's real name rather
  than whatever was typed into the image's alt text.

  THE PRICE UNIT WAS VERIFIED, NOT ASSUMED. `price` looked ambiguous (1637 could
  be $16.37 or $1,637), so it was measured on the live page: base total $5,729,
  selected "Top Gun 5 Burner Conv LP" whose price is 6516, and the total became
  $12,245. 5729 + 6516 = 12245, so **price is a whole-dollar ADDITIVE delta**.
  That is why it renders as "+$6,516" and why zero-priced values (every countertop
  and finish on that product) correctly show no price at all.

  GOTCHA for anyone extending this: `inst.setValue()` does NOT update the app's UI
  or its running total - only a real click on the `label.mw-label` does. Do not use
  setValue to drive the configurator.

  NO MutationObserver ON PURPOSE. The app emits a real `change` event, so there is
  no need to watch the DOM. An observer here would be watching a subtree this
  script also writes into, which is precisely the feedback loop that cost this
  theme a request per second once already (see sections/bp-product-offers.liquid).
  render() is idempotent regardless, as a second line of defence.
*/
(function () {
  var CURRENCY =
    (window.mwApoInit && window.mwApoInit.shop_currency) || 'USD';

  function money(dollars) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: CURRENCY,
        maximumFractionDigits: 0
      }).format(dollars);
    } catch (e) {
      return '$' + Math.round(dollars).toLocaleString();
    }
  }

  /*
    The option id is only discoverable from the radio's name: mw_opts[o_2484349].

    Parsed with string indexes rather than a regex ON PURPOSE. This file is
    deployed through `themeFilesUpsert`, whose GraphQL block strings do not
    process escapes, so any backslash-escaped bracket in a pattern can arrive in
    the served asset with the backslash doubled, and silently stop matching. That
    happened on the first deploy of this file: the CSS applied, every caption was
    missing, and the served bundle carried a doubled backslash before the bracket.
    Indexing has no backslashes at all, so the hazard is gone for good.
  */
  function optionIdOf(group) {
    var input = group.querySelector('.mw-option__control');
    if (!input) return null;
    var name = input.getAttribute('name') || '';
    var open = name.indexOf('[');
    var close = name.indexOf(']', open);
    if (open === -1 || close === -1) return null;
    return name.slice(open + 1, close);
  }

  function render() {
    var wrote = 0;
    var inst = window.mwApo && window.mwApo.getInstance && window.mwApo.getInstance();
    if (!inst || !inst.getValues) return wrote;

    var groups = document.querySelectorAll('.mw-option');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];

      /*
        SWATCH GROUPS ONLY.

        Same regression as the width rule in the CSS: the app renders `radio`,
        `checkbox` and `select` groups with the same `.mw-option__value` wrapper,
        but those ALREADY show their own text label and price - the client's
        screenshot had "Bar Counter on Right" printed twice and "+$69" under a
        label that already read "(+$69.00 USD)". Captions only make sense where
        the app shows an image and no text, which is the swatch type.
      */
      if (group.className.toString().indexOf('mw-option--type-swatch') === -1) continue;

      var optionId = optionIdOf(group);
      if (!optionId) continue;

      var values;
      try {
        values = inst.getValues(optionId) || [];
      } catch (e) {
        continue;
      }

      var byId = {};
      for (var i = 0; i < values.length; i++) byId[values[i].id] = values[i];

      var tiles = group.querySelectorAll('.mw-option__value');
      for (var t = 0; t < tiles.length; t++) {
        var tile = tiles[t];
        var value = byId[tile.getAttribute('data-value-id')];
        if (!value) continue;

        var name = value.title || '';
        var price = value.price > 0 ? money(value.price) : '';
        var signature = name + '|' + price;

        var cap = tile.querySelector('.bp-cfg__cap');

        /* Idempotent: identical content means no write, so nothing reflows and
           no event can be re-triggered by our own DOM changes. */
        if (cap && cap.getAttribute('data-sig') === signature) continue;

        if (!cap) {
          cap = document.createElement('span');
          cap.className = 'bp-cfg__cap';
          /* Appended to the VALUE, not into the app's <label>: a caption inside
             the label would become part of the click target, so reading a name
             could select the option. */
          tile.appendChild(cap);
        }

        cap.textContent = name;
        if (price) {
          var priceEl = document.createElement('span');
          priceEl.className = 'bp-cfg__cap-price';
          priceEl.textContent = '+' + price;
          cap.appendChild(priceEl);
        }
        cap.setAttribute('data-sig', signature);
        wrote++;
      }
    }
    return wrote;
  }

  /* ------------------------------------------------------------------------
     REQUIRED CHOICES, MADE VISIBLE.

     The site audit's CRITICAL finding: "selecting an option causes new required
     fields to appear ABOVE the swatch grid, shifting layout upward. A customer's
     next click can land on a field that just appeared instead of the one
     intended ... The site does not catch this until Add to Cart is clicked."

     Reproduced and measured on the 5 Foot Modern Linear island. Its options are:

       Select your base finish            required, visible
       Select your countertop finish      required, HIDDEN at load
       Select your doors & drawers finish required, HIDDEN at load
       Select Your 32 Inch Grill Package  optional
       Select Your Fuel Type              optional

     The two hidden ones are required AND sit before the grill package in DOM
     order, so revealing one pushes everything below it down. Selecting a base
     finish moved the Grill Package group by 1,169px, and the app ALSO moved the
     window (scrollY 1500 -> 825).

     WHAT THIS DOES NOT DO, deliberately: it does not try to suppress or reverse
     that scroll. The reflow and the scroll are both driven by the app's own code,
     and fighting it risks a worse bug than the one being fixed (for instance
     scrolling away from a field the app just chose to reveal on purpose).

     What it does instead is remove the SILENCE, which is the part that actually
     costs an order: a live list of outstanding required choices sits next to Add
     to cart, so a shopper is never first told at the button, and a newly revealed
     group is briefly highlighted so the movement is explained rather than
     mysterious.
     ------------------------------------------------------------------------ */

  var wasHidden = {};

  function optionGroups() {
    var map = {};
    var groups = document.querySelectorAll('.mw-option');
    for (var i = 0; i < groups.length; i++) {
      var oid = optionIdOf(groups[i]);
      if (oid) map[oid] = groups[i];
    }
    return map;
  }

  /* No regex here either - see the note on optionIdOf above. Same deploy hazard,
     same fix: plain string work only. */
  function shortLabel(title) {
    var t = String(title || '').trim();
    var lower = t.toLowerCase();
    var prefixes = ['select your ', 'select the ', 'select ', 'selection '];
    for (var i = 0; i < prefixes.length; i++) {
      if (lower.indexOf(prefixes[i]) === 0) return t.slice(prefixes[i].length);
    }
    return t;
  }

  function renderStatus() {
    var inst = window.mwApo && window.mwApo.getInstance && window.mwApo.getInstance();
    if (!inst || !inst.getOptions) return;

    /*
      THE ONE THEME-DEPENDENT SELECTOR IN THIS FILE, and it is load-bearing. If it
      misses, renderStatus() returns early and the required-choices checklist -
      the audit's CRITICAL fix - vanishes silently, with no error anywhere.

      That is exactly what the Dawn -> Turbo port hit. Dawn wraps the buy buttons
      in `.product-form__buttons`; Turbo has no such class and uses
      `.purchase-details__buttons`. Both verified in the live DOM on their own
      theme, which is the only way this kind of miss shows up at all.

      Candidates are tried in order, first hit wins, so this single file now
      serves either theme instead of needing a per-theme fork. Keep the list
      most-specific first, and ADD to it rather than replacing entries, so moving
      themes again cannot quietly drop the checklist.
    */
    var HOSTS = [
      '.purchase-details__buttons',
      '.product-form__buttons',
      '.shopify-payment-button',
      'form[action*="/cart/add"] [type="submit"]'
    ];
    var host = null;
    for (var h = 0; h < HOSTS.length; h++) {
      host = document.querySelector(HOSTS[h]);
      if (host) break;
    }
    /* parentNode is required because the box is inserted BEFORE its anchor. */
    if (!host || !host.parentNode) return;

    var selections = inst.getSelections() || [];
    var groups = optionGroups();
    var outstanding = [];
    var lockedCount = 0;

    var options = inst.getOptions();
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      if (!o.required) continue;

      var group = groups[o.id];
      var visible = group ? getComputedStyle(group).display !== 'none' : false;

      var chosen = false;
      var ids = o.valueIds || [];
      for (var v = 0; v < ids.length; v++) {
        if (selections.indexOf(ids[v]) !== -1) { chosen = true; break; }
      }

      /* Highlight a group the moment it stops being hidden, so the shopper can
         see WHY the page just moved. */
      if (group) {
        if (wasHidden[o.id] === true && visible) {
          group.classList.add('bp-cfg-revealed');
          setTimeout(function (g) {
            return function () { g.classList.remove('bp-cfg-revealed'); };
          }(group), 2600);
        }
        wasHidden[o.id] = !visible;
      }

      if (chosen) continue;
      if (visible) outstanding.push(shortLabel(o.title));
      else lockedCount++;
    }

    var box = document.querySelector('.bp-cfg-status');
    if (!box) {
      box = document.createElement('div');
      box.className = 'bp-cfg-status';
      box.setAttribute('role', 'status');
      /*
        INSERT ABOVE THE WHOLE BUY ROW, NOT INSIDE IT.

        On Turbo the host (.purchase-details__buttons) is a flex ITEM of
        .purchase-details, a nowrap flex ROW that also holds the quantity
        stepper. Inserted beside the host, this box became a third column: a
        102px-wide, 122px-tall sliver wedged between the stepper and Add to
        cart (measured 2026-09-10 on LBK710). Placing it before the row makes
        it a normal full-width block above the stepper and button. Dawn has no
        .purchase-details, so the fallback is the original behaviour there.
      */
      var anchor = host.closest('.purchase-details') || host;
      anchor.parentNode.insertBefore(box, anchor);
    }

    var html;
    if (outstanding.length) {
      html = '<strong>Still to choose:</strong> ' + outstanding.join(', ');
      box.setAttribute('data-state', 'todo');
    } else if (lockedCount > 0) {
      /*
        Honest about a genuinely odd state, measured at load on the 5 Foot Modern
        Linear island: the base finish arrives PRE-SELECTED, while both other
        required options are still hidden. So nothing is actionable, yet the
        configuration is incomplete - and the app only reveals the next option
        once the shopper actively changes a selection, not from the default.

        Saying "all set" here would be a lie that surfaces as an error on Add to
        cart, which is the exact failure the audit reported. Saying "one more
        appears once these are set" was also wrong, because everything visible
        already IS set. This states only what is verifiable: how many required
        choices remain, and that they appear as selections are made.
      */
      html =
        lockedCount === 1
          ? '1 more required choice appears as you make your selections.'
          : lockedCount + ' more required choices appear as you make your selections.';
      box.setAttribute('data-state', 'locked');
    } else {
      html = 'All required choices made.';
      box.setAttribute('data-state', 'done');
    }

    /* Idempotent for the same reason render() is. */
    if (box.innerHTML !== html) box.innerHTML = html;
  }

  /* ------------------------------------------------------------------------
     PROGRESSIVE DISCLOSURE: turn the wall of options into steps.

     Client, 2026-09-01, about the Cal Flame islands: "the whole checkout process
     and selecting the finishes and add on options is not good and confusing ...
     on the LBK 870 page there are four swatches that say finishes are those
     countertop or basement material I don't even know ... on the LBK710 there
     are eight different grills to choose from three side burners, nine different
     door and drawer combinations and 11 different accessories. It's just way too
     much information and we need to figure out a way to display all of that
     information in a cleaner and easier to understand way."

     He asked to DISPLAY it better, not to remove it, so nothing here deletes an
     option. Measured on the product he named (handle cal-flame-7-ft-bbq-island-lbk710,
     titled BBK710) the page renders TEN option groups and SEVENTY values as one
     flat, unlabelled column, in this DOM order:

       1  FINISHES               swatch    4    optional, all $0
       2  Gas Type               radio     2
       3  Countertop Tile        swatch   19    REQUIRED
       4  Base Material          swatch   10    REQUIRED
       5  Electrical             checkbox  1
       6  Grills                 checkbox  8
       7  Side Burners           checkbox  3
       8  Doors and Drawers      checkbox 10
       9  Grill Accessories      checkbox 11
      10  Backyard Delivery      radio     2    REQUIRED

     Two things follow from that list. The shopper meets a vague, free, optional
     "FINISHES" group BEFORE either of the two required finish choices, which is
     exactly the confusion the client reported. And a required delivery question
     sits below seventy values, where nobody scrolls.

     WHAT THIS DOES. Every group is sorted into a named step, the steps are
     ordered decision-first, and the purely optional add-on groups collapse into
     labelled accordions that state how many options they hold and what has been
     picked. The client's page goes from seventy tiles on arrival to four
     headings, with every one of those tiles still one tap away.

     THE INVARIANT, and it is the whole safety story: a REQUIRED group is never
     put inside an accordion. Classification here is keyword based, so it can be
     wrong on a product nobody has looked at - but the worst a wrong bucket can
     do is file a group under a heading a human would have chosen differently. It
     can never hide something the shopper must answer. See placeGroup().

     WHY MOVING THE APP'S NODES IS SAFE. The groups are moved, not cloned, and
     they stay inside the same form[action="/cart/add"], so the app keeps
     ownership of its own inputs and form serialisation is untouched. Verified
     end to end after this shipped: configure, add to cart, correct line price.

     SELF HEALING RATHER THAN AUTHORITATIVE. The app re-renders its container when
     its own rules reveal or hide a group. Instead of trying to prevent that, every
     pass re-checks each group's parent and moves it back only if it has drifted,
     which is a no-op on a settled page. There is still no MutationObserver here,
     for the reason given at the top of this file.
     ------------------------------------------------------------------------ */

  /* Steps are only worth the chrome when there is genuinely a wall to break up.
     A two or three option product reads better as the plain list the app ships. */
  var STEPS_MIN_GROUPS = 4;

  /* Display order, top to bottom. */
  var STEP_ORDER = ['finish', 'grill', 'other', 'addons', 'delivery'];

  var STEP_TITLE = {
    finish: 'Choose your finishes',
    grill: 'Choose your grill',
    other: 'Other choices',
    addons: 'Add features',
    delivery: 'Delivery'
  };

  var STEP_NOTE = {
    finish: 'The two surfaces you actually see: the countertop and the island base.',
    grill: '',
    other: '',
    addons: 'All optional. Open a section to see what fits this island.',
    delivery: ''
  };

  /*
    Classification, first match wins, so THE ORDER OF THIS LIST IS THE RULE.

    'finish' is tested before 'addons' on purpose. The Designer Series islands
    have a REQUIRED group called "Select your doors and drawers finish"; matched
    against 'door' first it would be filed as an add-on, which is the wrong
    heading. Testing 'finish' first files it with the other finishes.

    'addons' is tested before 'grill' for the mirror reason: "Grill Accessories
    and Lightning" contains the word grill but is plainly an accessory group.

    Substrings are deliberately conservative. 'ice bucket' rather than 'ice',
    because 'ice' is inside both "price" and "choice".
  */
  var STEP_MATCH = [
    ['delivery', ['delivery', 'shipping', 'freight', 'install']],
    ['finish', ['countertop', 'counter top', 'base material', 'finish', 'tile',
                'stucco', 'panel', 'stone', 'veneer', 'colour', 'color']],
    ['addons', ['accessor', 'lighting', 'lightning', 'side burner', 'sideburner',
                'door', 'drawer', 'electrical', 'refrigerat', 'oven', 'sink',
                'cocktail', 'ice bucket', 'umbrella', 'sound', 'warmer',
                'storage', 'bar center', 'trash', 'add on', 'add-on']],
    ['grill', ['grill', 'gas type', 'fuel', 'burner', 'pizza', 'griddle']]
  ];

  function stepFor(title, required) {
    var t = String(title || '').toLowerCase();
    for (var i = 0; i < STEP_MATCH.length; i++) {
      var words = STEP_MATCH[i][1];
      for (var w = 0; w < words.length; w++) {
        if (t.indexOf(words[w]) !== -1) return STEP_MATCH[i][0];
      }
    }
    /* Unrecognised: a required group gets its own visible step rather than being
       guessed into someone else's heading. An optional one can safely fold away. */
    return required ? 'other' : 'addons';
  }

  function isVisible(el) {
    return getComputedStyle(el).display !== 'none';
  }

  function buildStep(key, wrap) {
    var step = document.createElement('section');
    step.className = 'bp-cfg-step';
    step.setAttribute('data-step', key);

    var head = document.createElement('h3');
    head.className = 'bp-cfg-step__h';
    var num = document.createElement('span');
    num.className = 'bp-cfg-step__n';
    num.setAttribute('aria-hidden', 'true');
    var label = document.createElement('span');
    label.className = 'bp-cfg-step__t';
    label.textContent = STEP_TITLE[key] || '';
    head.appendChild(num);
    head.appendChild(label);
    step.appendChild(head);

    if (STEP_NOTE[key]) {
      var note = document.createElement('p');
      note.className = 'bp-cfg-step__note';
      note.textContent = STEP_NOTE[key];
      step.appendChild(note);
    }

    var body = document.createElement('div');
    body.className = 'bp-cfg-step__body';
    step.appendChild(body);

    wrap.appendChild(step);
    return step;
  }

  /*
    Summary line for a collapsed group. Before anything is picked it answers "how
    much is behind this heading, and what does it start at". Once something is
    picked it answers "what did I add, and what did it cost me", which is the
    question a closed accordion would otherwise hide.
  */
  function accSummary(inst, item, acc) {
    var vals = [];
    try {
      vals = inst.getValues(item.oid) || [];
    } catch (e) {
      vals = [];
    }

    var chosen = 0;
    var added = 0;
    var cheapest = 0;
    for (var v = 0; v < vals.length; v++) {
      var val = vals[v];
      if (val.selected) {
        chosen++;
        added += val.price || 0;
      }
      if (val.price > 0 && (cheapest === 0 || val.price < cheapest)) cheapest = val.price;
    }

    var text;
    if (chosen > 0) {
      text = chosen + ' selected';
      if (added > 0) text += ', +' + money(added);
    } else {
      text = vals.length + (vals.length === 1 ? ' option' : ' options');
      if (cheapest > 0) text += ', from ' + money(cheapest);
    }

    var nameEl = acc.querySelector('.bp-cfg-acc__name');
    var metaEl = acc.querySelector('.bp-cfg-acc__meta');
    var name = shortLabel(item.title);
    if (nameEl && nameEl.textContent !== name) nameEl.textContent = name;
    if (metaEl && metaEl.textContent !== text) metaEl.textContent = text;

    var state = chosen > 0 ? '1' : '0';
    if (acc.getAttribute('data-chosen') !== state) acc.setAttribute('data-chosen', state);

    /* Open once, on first sight, if the shopper already has something in here -
       for instance a value the app pre-selects. Never forced afterwards, because
       that would slam an accordion shut or open under someone's hand. */
    if (!acc.getAttribute('data-init')) {
      acc.setAttribute('data-init', '1');
      if (chosen > 0) acc.open = true;
    }
  }

  function placeGroup(inst, item, body) {
    /* THE INVARIANT. Optional groups in the add-ons step collapse; a required
       group is left in the open flow no matter which bucket it landed in. */
    var collapse = item.step === 'addons' && !item.required && item.oid;

    if (!collapse) {
      if (item.el.parentNode !== body) body.appendChild(item.el);
      /* Only groups in the open flow are clamped. Inside an accordion the
         shopper has already asked to see the group, and a closed accordion has
         no layout to measure rows from. */
      clampGroup(inst, item);
      return;
    }

    var acc = body.querySelector('[data-acc="' + item.oid + '"]');
    if (!acc) {
      acc = document.createElement('details');
      acc.className = 'bp-cfg-acc';
      acc.setAttribute('data-acc', item.oid);

      var sum = document.createElement('summary');
      sum.className = 'bp-cfg-acc__sum';

      var name = document.createElement('span');
      name.className = 'bp-cfg-acc__name';
      var meta = document.createElement('span');
      meta.className = 'bp-cfg-acc__meta';
      sum.appendChild(name);
      sum.appendChild(meta);

      acc.appendChild(sum);
      body.appendChild(acc);
    }

    if (item.el.parentNode !== acc) acc.appendChild(item.el);
    accSummary(inst, item, acc);

    /* A group the app has hidden by its own rules takes its accordion with it,
       so no empty heading is left behind promising options that are not there. */
    var want = isVisible(item.el) ? '' : 'none';
    if (acc.style.display !== want) acc.style.display = want;
  }

  /*
    TWO IDENTICAL GROUPS UNDER ONE HEADING.

    The Carmel Q 870 (handle cal-flame-l-shaped-design-your-own-lbk870) renders
    the group titled "FINISHES" TWICE - option ids o_3471662 and o_3468513 - and
    the two are indistinguishable: same title, same four values in the same order
    (Birch, Cloud, Mocha, Quake), every one of them free, both optional. That is
    the confusion the client hit head on: "there are four swatches that say
    finishes are those countertop or basement material I don't even know".

    Sorting the groups into steps makes it WORSE before it makes it better,
    because the duplicate pair ends up side by side under one heading where it is
    impossible to miss.

    This is a data bug and the real fix is deleting one of them in the MageWorx
    admin, which has been raised. Until that happens the second copy is hidden,
    under conditions strict enough that hiding it cannot remove a choice a
    shopper ever actually had:

      - identical title, ignoring case and surrounding space
      - identical value titles, same values in the same order
      - every value in both is free, so this can never change a price
      - neither group is required, so nothing can become unanswerable

    Two groups that pass all four are interchangeable by construction - a shopper
    picking between them is guessing - so the first is kept and the rest hidden.
    The moment the duplicate is deleted in the app this stops matching and stops
    doing anything, which is the property that makes it safe to leave in.
  */
  function duplicateGroupIds(inst, options) {
    var seen = {};
    var hide = {};

    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      if (o.required) continue;

      var values;
      try {
        values = inst.getValues(o.id) || [];
      } catch (e) {
        continue;
      }

      var free = true;
      var names = [];
      for (var v = 0; v < values.length; v++) {
        if (values[v].price > 0) free = false;
        names.push(String(values[v].title || ''));
      }
      if (!free || !names.length) continue;

      var key = String(o.title || '').trim().toLowerCase() + '||' + names.join('||');
      if (seen[key]) {
        hide[o.id] = true;
        if (window.console && console.info) {
          console.info('bp-configurator: hiding duplicate option group "' + o.title +
                       '" (' + o.id + '), identical to ' + seen[key] +
                       '. Delete one in the MageWorx admin.');
        }
      } else {
        seen[key] = o.id;
      }
    }
    return hide;
  }

  /*
    LONG SWATCH GROUPS GET A "SHOW ALL".

    The two REQUIRED finish groups are the longest on the page and they cannot be
    collapsed away, since a shopper has to answer both. Countertop Tile alone
    holds nineteen values. The product column on this theme is narrow enough to
    fit two tiles per row - measured at 282px on a 1280px desktop - so nineteen
    tiles is ten rows and roughly 1,500px of scrolling before the next question.

    Clamping to three rows keeps the group, its heading and a real sample of the
    choices on screen, and puts the rest one tap away. The count goes in the
    button so nobody has to wonder how much is behind it.

    The row height is MEASURED rather than assumed: tile heights change with the
    caption's line count, which changes with the value's name, so a fixed rem cap
    would slice a row in half on some products and leave a gap on others. Reading
    the fourth row's offsetTop gives exactly three rows on any of them.
  */
  var CLAMP_MIN_VALUES = 7;
  var CLAMP_ROWS = 3;

  function clampGroup(inst, item) {
    if (item.el.className.toString().indexOf('mw-option--type-swatch') === -1) return;

    var box = item.el.querySelector('.mw-option__container');
    if (!box) return;

    var tiles = box.querySelectorAll('.mw-option__value');
    if (tiles.length < CLAMP_MIN_VALUES) return;

    /* Nothing to measure while the group is off screen; the next pass will do it
       once it has a layout. */
    if (!box.offsetHeight) return;

    var btn = item.el.querySelector('.bp-cfg-more');
    if (!btn) {
      btn = document.createElement('button');
      /* type=button, or this becomes a second submit inside the cart form. */
      btn.type = 'button';
      btn.className = 'bp-cfg-more';
      btn.addEventListener('click', function () {
        var open = item.el.classList.toggle('bp-cfg-clamped--open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show fewer' : 'Show all ' + tiles.length;
      });
      box.parentNode.insertBefore(btn, box.nextSibling);

      item.el.classList.add('bp-cfg-clamped');
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Show all ' + tiles.length;

      /*
        Open once, at first sight, if something in here is already chosen -
        otherwise a value the app pre-selected could sit below the clamp and the
        group would read as unanswered. Only at init, so this never reopens a
        group the shopper deliberately closed.
      */
      var values = [];
      try {
        values = inst.getValues(item.oid) || [];
      } catch (e) {
        values = [];
      }
      for (var v = 0; v < values.length; v++) {
        if (values[v].selected) {
          item.el.classList.add('bp-cfg-clamped--open');
          btn.setAttribute('aria-expanded', 'true');
          btn.textContent = 'Show fewer';
          break;
        }
      }
    }

    /*
      Row tops, deduplicated: tiles on the same row share a top edge.

      MEASURED FROM THE BOX, NOT offsetTop. The first version of this used
      `tiles[t].offsetTop`, which is measured from the nearest POSITIONED
      ancestor - and `.mw-option__container` is not positioned, so the values
      came back relative to something further up the tree. On the Carmel Q 870
      that produced a clamp of 4,219px for a group only 3,085px tall, which
      would have done nothing at all while looking like it worked.

      Verified after the fix at 1280x900: Countertop Tile is 19 tiles, two per
      row, 10 rows, 1,711px tall, clamping to 532px.
    */
    var boxTop = box.getBoundingClientRect().top;
    var rows = [];
    for (var t = 0; t < tiles.length; t++) {
      var top = Math.round(tiles[t].getBoundingClientRect().top - boxTop);
      if (rows.indexOf(top) === -1) rows.push(top);
    }
    if (rows.length <= CLAMP_ROWS) {
      /* Short enough on this viewport that clamping would only add a pointless
         button - most likely a wide desktop fitting four tiles per row. */
      item.el.classList.remove('bp-cfg-clamped');
      btn.style.display = 'none';
      return;
    }

    item.el.classList.add('bp-cfg-clamped');
    btn.style.display = '';

    var cap = rows[CLAMP_ROWS] + 'px';
    if (box.style.getPropertyValue('--bp-clamp') !== cap) {
      box.style.setProperty('--bp-clamp', cap);
    }
  }

  function layoutSteps() {
    var host = document.querySelector('.mw-apo-container');
    if (!host) return;

    var inst = window.mwApo && window.mwApo.getInstance && window.mwApo.getInstance();
    if (!inst || !inst.getOptions) return;

    var groups = document.querySelectorAll('.mw-option');
    if (groups.length < STEPS_MIN_GROUPS) return;

    var meta = {};
    var options = [];
    try {
      options = inst.getOptions() || [];
    } catch (e) {
      return;
    }
    for (var i = 0; i < options.length; i++) meta[options[i].id] = options[i];

    var duplicates = duplicateGroupIds(inst, options);

    var wrap = host.querySelector('.bp-cfg-steps');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'bp-cfg-steps';
      host.insertBefore(wrap, host.firstChild);
    }

    var buckets = {};
    for (var g = 0; g < groups.length; g++) {
      var el = groups[g];
      var oid = optionIdOf(el);
      var o = oid ? meta[oid] : null;
      var title = o ? o.title : '';
      var required = o ? !!o.required : false;
      /* Hidden here rather than skipped, so it is still placed under a heading
         and simply has no layout - which is what the step and accordion
         visibility checks below already know how to handle. */
      if (oid && duplicates[oid]) {
        if (el.style.display !== 'none') el.style.display = 'none';
      }

      var key = stepFor(title, required);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push({ el: el, oid: oid, title: title, required: required, step: key });
    }

    var shown = 0;
    var placed = 0;
    for (var s = 0; s < STEP_ORDER.length; s++) {
      var stepKey = STEP_ORDER[s];
      var items = buckets[stepKey];
      if (!items || !items.length) continue;

      var step = wrap.querySelector('[data-step="' + stepKey + '"]');
      if (!step) step = buildStep(stepKey, wrap);
      var stepBody = step.querySelector('.bp-cfg-step__body');

      var anyVisible = false;
      for (var n = 0; n < items.length; n++) {
        placeGroup(inst, items[n], stepBody);
        if (isVisible(items[n].el)) anyVisible = true;
      }

      /*
        Re-establish display order, counting only the steps that EXIST rather than
        indexing into STEP_ORDER: a product with no delivery question and no
        add-ons still has to end up with its two steps in slots 0 and 1. Compared
        before writing, so this is a no-op on a settled page and never moves a
        node under a shopper's finger.
      */
      var slot = wrap.children[placed];
      if (slot !== step) wrap.insertBefore(step, slot || null);
      placed++;

      var hide = anyVisible ? '' : 'none';
      if (step.style.display !== hide) step.style.display = hide;
      if (!anyVisible) continue;

      /* Numbers count only the steps a shopper can actually see, so a product
         whose first step is entirely hidden by app rules never starts at "2". */
      shown++;
      var badge = step.querySelector('.bp-cfg-step__n');
      var label = String(shown);
      if (badge && badge.textContent !== label) badge.textContent = label;
    }
  }

  function paint() {
    /* Layout first: captions and the required-choices checklist both read the
       DOM, so the groups should already be sitting where they belong. */
    layoutSteps();
    var wrote = render();
    renderStatus();
    return wrote;
  }

  function start() {
    paint();

    var inst = window.mwApo && window.mwApo.getInstance && window.mwApo.getInstance();
    if (inst && inst.on) {
      /* Selections can hide or reveal values through the app's own rules, so the
         captions and the checklist are rebuilt after every change. */
      inst.on('change', paint);
      inst.on('ready', paint);
    }

    /*
      The app renders its swatches asynchronously, and on the Designer Series
      product `mwApo.ready` fired BEFORE the tiles existed - the first deploy
      produced zero captions there while working perfectly on a Cal Flame island.
      So retry briefly and stop as soon as a pass writes nothing, which is the
      idempotence signal that everything on screen is already captioned.
    */
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      var wrote = paint();
      if (attempts > 25 || (wrote === 0 && document.querySelector('.bp-cfg__cap'))) {
        clearInterval(timer);
      }
    }, 300);
  }

  if (window.mwApo && typeof window.mwApo.ready === 'function') {
    try {
      window.mwApo.ready(start);
    } catch (e) {
      start();
    }
  } else {
    /* The app's bundle may not have executed yet. Poll briefly rather than fail
       silently, then give up so this can never spin forever. */
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (window.mwApo && window.mwApo.getInstance) {
        clearInterval(timer);
        start();
      } else if (tries > 40) {
        clearInterval(timer);
      }
    }, 250);
  }
})();